import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../hooks/useTheme';

interface ThemeOption {
  key: 'light' | 'dark' | 'system';
  label: string;
  icon: string;
}

const ThemeSelector: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();

  const themeOptions: ThemeOption[] = [
    { key: 'light', label: t('themeSelector.light'), icon: '☀️' },
    { key: 'dark', label: t('themeSelector.dark'), icon: '🌙' },
    { key: 'system', label: t('themeSelector.system'), icon: '💻' },
  ];

  const handleThemeSelect = (selectedTheme: 'light' | 'dark' | 'system') => {
    setTheme(selectedTheme);
    setIsOpen(false);
  };

  const getCurrentThemeIcon = () => {
    const currentOption = themeOptions.find(option => option.key === theme);
    return currentOption?.icon || '💻';
  };

  const getCurrentThemeLabel = () => {
    const currentOption = themeOptions.find(option => option.key === theme);
    return currentOption?.label || t('themeSelector.system');
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-4 py-2 bg-white/60 dark:bg-neutral-800/60 backdrop-blur-sm rounded-full shadow-soft hover:shadow-medium transition-all duration-300"
      >
        <span className="text-lg">{getCurrentThemeIcon()}</span>
        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
          {getCurrentThemeLabel()}
        </span>
      </button>

      {isOpen && (
        <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 w-48 bg-white dark:bg-neutral-800 rounded-2xl shadow-large border border-neutral-200 dark:border-neutral-700 z-50">
          <div className="p-4">
            <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
              {t('themeSelector.chooseTheme')}
            </h3>
            <div className="space-y-2">
              {themeOptions.map((option) => (
                <button
                  key={option.key}
                  onClick={() => handleThemeSelect(option.key)}
                  className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors duration-200"
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-lg">{option.icon}</span>
                    <div className="text-left">
                      <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                        {option.label}
                      </div>
                    </div>
                  </div>
                  {theme === option.key && (
                    <svg className="w-4 h-4 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default ThemeSelector;
